import React, { useEffect, useState } from 'react';

interface MemberProfile {
  line_user_id: string;
  display_name: string;
  ranking_name: string;
  show_in_ranking: boolean;
  ranking_display_type: 'nickname' | 'initials' | 'anonymous';
  email: string;
  phone: string;
  birthday: string;
  gender: string;
  postal_code: string;
  address: string;
  receive_birthday_coupon: boolean;
  receive_gender_day_coupon: boolean;
  receive_campaign_info: boolean;
  profile_completed: boolean;
  admin_ranking_ban: number; // 0: 通常、1: 管理者によるBAN
}

interface MemberProfileSettingsProps {
  lineUserId: string;
}

export const MemberProfileSettings: React.FC<MemberProfileSettingsProps> = ({ lineUserId }) => {
  const [profile, setProfile] = useState<MemberProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    fetchProfile();
  }, [lineUserId]);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`/api/profile/member/${lineUserId}`);
      if (response.ok) {
        const data = await response.json();
        setProfile(data.profile || data); // APIレスポンスが { profile: {...} } の場合に対応
      }
    } catch (error) {
      console.error('プロフィール取得エラー:', error);
    } finally {
      setLoading(false);
    }
  };

  const updateRankingBan = async (banned: boolean) => {
    setUpdating(true);
    try {
      const response = await fetch(`/api/profile/member/${lineUserId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_ranking_ban: banned ? 1 : 0 })
      });
      if (response.ok) {
        await fetchProfile();
        alert(banned ? 'ランキング表示をBANしました' : 'ランキングBANを解除しました');
      } else {
        alert('更新に失敗しました');
      }
    } catch (error) {
      console.error('プロフィール更新エラー:', error);
      alert('更新に失敗しました');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">読み込み中...</div>
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="text-gray-500">プロフィール未登録</div>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-6">
      {/* ユーザー登録情報（閲覧専用） */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          📋 ユーザー登録情報（閲覧専用）
        </h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-sm text-gray-600">カード表示名</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.display_name || '未設定'}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-600">ランキング表示名</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.ranking_name || '未設定'}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-600">メールアドレス</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.email || '未設定'}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-600">電話番号</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.phone || '未設定'}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-600">生年月日</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.birthday || '未設定'}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-600">性別</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.gender === 'male' ? '男性' :
               profile.gender === 'female' ? '女性' :
               profile.gender === 'other' ? 'その他' : '未設定'}
            </div>
          </div>
        </div>

        <div className="mt-4 grid grid-cols-1 gap-4">
          <div>
            <label className="text-sm text-gray-600">郵便番号</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.postal_code || '未設定'}
            </div>
          </div>
          <div>
            <label className="text-sm text-gray-600">住所</label>
            <div className="mt-1 p-2 bg-white border rounded">
              {profile.address || '未設定'}
            </div>
          </div>
        </div>
      </div>

      {/* ランキング設定（管理者用） */}
      <div className="bg-blue-50 rounded-lg p-4 border-2 border-blue-200">
        <h3 className="text-lg font-semibold mb-4 flex items-center text-blue-800">
          🏆 ランキング設定（管理者用）
        </h3>

        <div className="space-y-3">
          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600 mb-2">ユーザー設定</div>
            <div className="flex items-center gap-2">
              <span className="font-medium">ランキング表示：</span>
              <span className={`px-2 py-1 rounded text-sm ${
                profile.show_in_ranking ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
              }`}>
                {profile.show_in_ranking ? 'ON' : 'OFF'}
              </span>
              {profile.show_in_ranking && (
                <span className="text-sm text-gray-500">
                  （{profile.ranking_display_type === 'nickname' ? 'ニックネーム' :
                     profile.ranking_display_type === 'initials' ? 'イニシャル' : '匿名'}）
                </span>
              )}
            </div>
          </div>

          <div className="bg-white p-3 rounded border">
            <div className="text-sm text-gray-600 mb-2">管理者によるランキング制御</div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-medium">
                  {profile.admin_ranking_ban === 1 ? (
                    <span className="text-red-600">🚫 ランキングBAN中</span>
                  ) : (
                    <span className="text-green-600">✅ ランキング表示可能</span>
                  )}
                </div>
                <div className="text-sm text-gray-500 mt-1">
                  {profile.admin_ranking_ban === 1 ? (
                    'このユーザーはランキングに表示されません'
                  ) : (
                    'ユーザー設定に従ってランキングに表示されます'
                  )}
                </div>
              </div>
              <button
                onClick={() => updateRankingBan(profile.admin_ranking_ban !== 1)}
                disabled={updating}
                className={`px-4 py-2 rounded font-medium transition-colors ${
                  profile.admin_ranking_ban === 1
                    ? 'bg-green-600 hover:bg-green-700 text-white'
                    : 'bg-red-600 hover:bg-red-700 text-white'
                } disabled:opacity-50 disabled:cursor-not-allowed`}
              >
                {updating ? '更新中...' :
                 profile.admin_ranking_ban === 1 ? 'BANを解除' : 'ランキングをBANする'}
              </button>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <div className="text-sm text-yellow-800">
              <strong>⚠️ 注意事項：</strong>
              <ul className="list-disc list-inside mt-1 space-y-1">
                <li>ランキングBANは、禁止用語などルール違反時に使用してください</li>
                <li>ユーザーが「ランキング表示ON」にしていても、BAN中は表示されません</li>
                <li>BANを解除すると、ユーザー設定に従って再度表示されます</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* 通知設定 */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          🔔 通知設定
        </h3>
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded ${profile.receive_birthday_coupon ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span>誕生日クーポンを受け取る：{profile.receive_birthday_coupon ? 'ON' : 'OFF'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded ${profile.receive_gender_day_coupon ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span>性別限定クーポンを受け取る：{profile.receive_gender_day_coupon ? 'ON' : 'OFF'}</span>
          </div>
          <div className="flex items-center gap-2">
            <span className={`w-4 h-4 rounded ${profile.receive_campaign_info ? 'bg-green-500' : 'bg-gray-300'}`} />
            <span>キャンペーン情報を受け取る：{profile.receive_campaign_info ? 'ON' : 'OFF'}</span>
          </div>
        </div>
      </div>

      {/* プロフィール完成度 */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          📊 プロフィール完成度
        </h3>
        <div className="flex items-center gap-2">
          <span className={`px-3 py-1 rounded font-medium ${
            profile.profile_completed ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
          }`}>
            {profile.profile_completed ? '✅ 完了' : '⚠️ 未完了'}
          </span>
        </div>
      </div>
    </div>
  );
};

export default MemberProfileSettings;
